import { ThemeEvents } from '@theme/events';

/**
 * Positions the PLP/search USP bar (`.product-grid__item--usp`) at the end of the
 * first visible product row. The product grid uses a responsive `auto-fill` column
 * count, so the correct DOM position depends on the live column count — this reads
 * it from the computed grid template and re-runs on resize, infinite-scroll append
 * and filter updates.
 */

/**
 * Move the USP bar so it sits right after the first full row of product cards.
 *
 * @param {HTMLElement} grid - The `.product-grid` <ul>
 */
const reposition = (grid) => {
  const usp = grid.querySelector(':scope > .product-grid__item--usp');
  if (!usp) return;

  const template = getComputedStyle(grid).gridTemplateColumns;
  const columns = template && template !== 'none' ? template.split(' ').filter(Boolean).length : 1;

  const products = Array.from(grid.children).filter(
    (el) => el.classList.contains('product-grid__item') && el !== usp
  );

  // Reference node = first card of the second row (or end of list if only one row).
  const reference = products[columns] ?? null;
  if (usp.nextElementSibling === reference) return; // already in place

  grid.insertBefore(usp, reference);
};

/** @type {WeakSet<HTMLElement>} */
const observed = new WeakSet();

/**
 * Wire up observers for a single grid that contains a USP bar.
 *
 * @param {HTMLElement} grid
 */
const observe = (grid) => {
  if (observed.has(grid)) return;
  observed.add(grid);

  let frame = 0;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => reposition(grid));
  };

  schedule();

  new ResizeObserver(schedule).observe(grid);
  new MutationObserver(schedule).observe(grid, { childList: true });
};

/** Scan the document for grids that have (or just received) a USP bar. */
const scan = () => {
  for (const usp of document.querySelectorAll('.product-grid__item--usp')) {
    const grid = usp.closest('.product-grid');
    if (grid instanceof HTMLElement) observe(grid);
  }
};

scan();

// Filtering re-renders the grid HTML, so re-scan (and the per-grid MutationObserver
// handles the in-place card swaps once the grid is observed).
document.addEventListener(ThemeEvents.FilterUpdate, () => requestAnimationFrame(scan));
